import { Component, OnInit, AfterViewInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs';
import { formatarData, formatarPlaca } from 'src/app/core/utils/utilities';
import {
  IGetRelatorioFinanceiroPatioListParams,
  IGetRelatorioFinanceiroPatioListResponse,
  IGetRelatorioFinanceiroPatioItem
} from 'src/app/services/ReportService/report.model';
import { ReportService } from 'src/app/services/ReportService/report.service';
import { DestinatarioService } from 'src/app/services/DestinatarioService/destinatario.service';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';

@Component({
  selector: 'app-relatorio-financeiro-patio',
  templateUrl: './relatorio-financeiro-patio.component.html',
  styleUrls: ['./relatorio-financeiro-patio.component.scss']
})
export class RelatorioFinanceiroPatioComponent implements OnInit, AfterViewInit {
  form = this.fb.group({
    PK_SRVC_ENTR: [null as number | null],
    TX_PLCA_CAVA: [null as string | null],
    NM_EPSA: [null as string | null],
    VL_SRVC: [null as number | null],
    NU_TIPO_PGMT: [null as number | null],
    DT_INIC: [null as string | null],
    DT_FIM: [null as string | null],
    FK_DTNR: ['' as number | string | null]
  });

  isLoading = false;
  numeroPagina = 0;
  maxPages = 1;
  tableData: Array<any> = [];
  valorTotalFiltros = 0;

  terminais: { name: string; value: number | string }[] = [
    { name: 'Todos os terminais', value: '' }
  ];

  readonly formasPagamento = [
    { value: null, name: 'Todos' },
    { value: 1, name: 'Cartão de Crédito' },
    { value: 19, name: 'Cartão de Débito' },
    { value: 9, name: 'Dinheiro' },
    { value: 27, name: 'PIX' },
    { value: 28, name: 'A Prazo' }
  ];

  pesquisaParams = {
    PAGE_NUMBER: 1,
    PAGE_SIZE: 20
  };

  tableColumns = [
    { header: 'Cód. Serviço', key: 'codigoServico', type: 'text' },
    { header: 'Data do Serviço', key: 'dataServico', type: 'text' },
    { header: 'Serviço', key: 'servico', type: 'text' },
    { header: 'Valor', key: 'valorServico', type: 'text' },
    { header: 'Forma de Pagamento', key: 'formaPagamento', type: 'text' },
    { header: 'Placa', key: 'placa', type: 'text' },
    { header: 'Motorista', key: 'motorista', type: 'text' },
    { header: 'Transportadora', key: 'transportadora', type: 'text' },
    { header: 'Terminal', key: 'terminal', type: 'text' }
  ];

  constructor(
    private fb: FormBuilder,
    private reportService: ReportService,
    private destinatarioService: DestinatarioService,
    private appExportBase64: AppExportBase64
  ) {}

  ngOnInit(): void {
    this.destinatarioService.getDestinatarios().subscribe(response => {
      this.terminais = [
        { name: 'Todos os terminais', value: '' },
        ...response.Destinatarios.map(d => ({
          name: d.NM_DTNR,
          value: d.PK_DTNR
        }))
      ];
    });
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.pesquisaParams.PAGE_NUMBER = 1;
      this.numeroPagina = 0;
      this.loadData();
    });
  }

  mudarPagina(pagina: number): void {
    this.pesquisaParams.PAGE_NUMBER = pagina + 1;
    this.numeroPagina = pagina;
    this.loadData();
  }

  private createPayload(): IGetRelatorioFinanceiroPatioListParams {
    const fkDtnr = this.form.controls.FK_DTNR.value;
    return {
      PK_SRVC_ENTR: this.form.controls.PK_SRVC_ENTR.value ?? undefined,
      TX_PLCA_CAVA: this.form.controls.TX_PLCA_CAVA.value ?? undefined,
      NM_EPSA: this.form.controls.NM_EPSA.value ?? undefined,
      VL_SRVC: this.form.controls.VL_SRVC.value ?? undefined,
      NU_TIPO_PGMT: this.form.controls.NU_TIPO_PGMT.value ?? undefined,
      DT_INIC: this.form.controls.DT_INIC.value ?? undefined,
      DT_FIM: this.form.controls.DT_FIM.value ?? undefined,
      FK_DTNR: fkDtnr !== '' && fkDtnr !== null ? Number(fkDtnr) : undefined,
      PAGE_NUMBER: this.pesquisaParams.PAGE_NUMBER,
      PAGE_SIZE: this.pesquisaParams.PAGE_SIZE
    };
  }

  loadData(): void {
    this.isLoading = true;
    const payload = this.createPayload();

    this.reportService.GetRelatorioFinanceiroPatio(payload).subscribe({
      next: (response: IGetRelatorioFinanceiroPatioListResponse) => {
        this.tableData = response.ITEMS.map((item: IGetRelatorioFinanceiroPatioItem) => ({
          codigoServico: item.PK_SRVC_ENTR ?? '---',
          dataServico: formatarData(item.DT_SRVC_ENTR) ?? '---',
          servico: item.TX_TIPO_SRVC ?? '---',
          valorServico: item.VL_SRVC != null
            ? `R$ ${Number(item.VL_SRVC).toFixed(2).replace('.', ',')}`
            : 'R$ 0,00',
          formaPagamento: item.TX_TIPO_PGMT ?? '---',
          placa: formatarPlaca(item.TX_PLCA_CAVA) ?? '---',
          motorista: item.NM_MTRT && item.NM_MTRT !== '----' ? item.NM_MTRT : '---',
          transportadora: item.NM_TRANSPORTADORA ?? '---',
          terminal: item.NM_DTNR ?? '---'
        }));
        this.valorTotalFiltros = response.ITEMS[0]?.VL_TOTAL ?? 0;
        this.maxPages = response.TOTAL_PAGES;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  exportarExcel(): void {
    this.isLoading = true;
    const payload = this.createPayload();

    this.reportService.GetReportFinanceiroPatio({
      PK_SRVC_ENTR: payload.PK_SRVC_ENTR,
      TX_PLCA_CAVA: payload.TX_PLCA_CAVA,
      NM_EPSA: payload.NM_EPSA,
      VL_SRVC: payload.VL_SRVC,
      NU_TIPO_PGMT: payload.NU_TIPO_PGMT,
      DT_INIC: payload.DT_INIC,
      DT_FIM: payload.DT_FIM,
      FK_DTNR: payload.FK_DTNR
    }).subscribe({
      next: response => {
        this.appExportBase64.downloadExcel(response.ReportBase64, 'Relatório Financeiro Pátio.xlsx');
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  exportarPDF(): void {
    this.isLoading = true;
    const payload = this.createPayload();

    this.reportService.GetReportFinanceiroPatioPdf({
      PK_SRVC_ENTR: payload.PK_SRVC_ENTR,
      TX_PLCA_CAVA: payload.TX_PLCA_CAVA,
      NM_EPSA: payload.NM_EPSA,
      VL_SRVC: payload.VL_SRVC,
      NU_TIPO_PGMT: payload.NU_TIPO_PGMT,
      DT_INIC: payload.DT_INIC,
      DT_FIM: payload.DT_FIM,
      FK_DTNR: payload.FK_DTNR
    }).subscribe({
      next: response => {
        this.appExportBase64.downloadPDF(response.ReportBase64, 'Relatório Financeiro Pátio.pdf');
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  get valorTotalFormatado(): string {
    return `R$ ${this.valorTotalFiltros.toFixed(2).replace('.', ',')}`;
  }
}
